import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Divider, List, ListItem, ListItemButton, ListItemText, Collapse, Tooltip, Typography } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useContext } from 'react';
import { observer } from 'mobx-react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import MUILink from '@mui/material/Link';
import { route } from '/@/app/routes';
import { AppContext } from '/@/app/AppContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import ConstructionIcon from '@mui/icons-material/Construction';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { SubscriptionNote } from '/@/app/components/subscription/SubscriptionIcon';
import { TipOfTheDay } from '/@/app/components/TipOfTheDay';

import { InstallComponent } from '/@/app/PWA/InstallComponent';
import { UpdateAnnouncements } from '/@/app/components/UpdateAnnouncements';
import { SupportLinks } from '/@/app/components/SupportLinks';
import { useAuth } from "/@/Auth";
import { useApi } from "/@/app/Api";

//
// The content of the main drawer / sidebar.
//

export const AccountActions = () => {
    const { t } = useTranslation();
    const { handleAuthFailure, isAuthenticated } = useAuth();
    const { userData } = useContext(AppContext);
    const { payment_system_enabled }  = useContext(AppContext).bootstrapData;
    const [logoutConfirm,setLogoutConfirm]=React.useState({open:false})
    const backend_url=window.appConfig.backend_url;

    return (
        <>
            <Divider sx={{mt: 3, mb: 2}} />
            <Grid container spacing={1}>

                <Grid item xs={12}>
                    <Button component={Link} to={route("/profile")}><AccountCircleIcon sx={{ mr: 1 }}/> {t('Аккаунт')}</Button>
                </Grid>
                {payment_system_enabled &&
                    <Grid item xs={12}>
                        <Button href={route('/subscriptions/')}><CreditCardIcon sx={{ mr: 1 }}/> {t("Подписки")}</Button>
                        <SubscriptionNote />
                    </Grid>
                }
                <Grid item xs={12}>
                    <Button variant="text" onClick={e=>setLogoutConfirm({open:true})}><LogoutIcon sx={{ mr: 1 }} /> {t("Выйти")}</Button>
                </Grid>

                {userData.is_staff || userData.is_superuser ? (
                    <Grid item xs={12}>
                        <Divider sx={{mt: 3, mb: 2}} />
                        <Button href={backend_url+'/admin/'} sx={{ display: "flex", flexFlow: "row nowrap", justifyContent: "center" }}>
                            <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                            <Box sx={{ fontSize: "14px", lineHeight: "1.1", maxWidth: "150px" }}>{t("Панель Администратора")}</Box>
                        </Button>
                        <Button component={Link} to={'/test'}>{t("Test Page")}</Button>
                    </Grid>
                ) : null
                }
            </Grid>

<Dialog open={logoutConfirm.open}>
<DialogTitle>{t('Подтвердите выход')}</DialogTitle>
<DialogContent>
<Box sx={{mb:2}}><Typography sx={{fontSize:'1.2rem'}}>{t('Вы действительно хотите выйти из Nestarenie.Expert ?')}</Typography></Box>
</DialogContent>
<DialogActions>
<Button onClick={e=>setLogoutConfirm({open:false})}>{t('Отмена')}</Button>
<Button onClick={e=>handleAuthFailure({ text: 'Вы вышли', type: "info" })} variant="contained">{t('Выйти')}</Button>
</DialogActions>
</Dialog>

        </>
    )
}

const DebugToosLink = () => {
    const { t } = useTranslation();
    const { userData } = useContext(AppContext);
    if (userData.is_staff || userData.is_superuser) {
        return (
            <Box>
                <Divider sx={{mt: 3, mb: 2}} />
                <Button variant="text" component={Link} to={route("/tester/")}>
                    <ConstructionIcon sx={{ mr: 1 }}/>
                    <Box sx={{ fontSize: "14px", lineHeight: "1.1", maxWidth: "150px" }}>{t("Панель Тестировщика")}</Box>
                </Button>
            </Box>
        )
    }
    return null;
}

export const DrawerContent = observer(({onItemClick, userFlowState}) => {
    const { t, i18n } = useTranslation();
    const api = useApi();
    const { showDynamicsPage, featureEnabled, bootstrapData } = useContext(AppContext);
    const theme = useTheme();
    const location = useLocation();

    const submenuOffset = 6;
    const [labTestsExpanded, setLabTestsExpanded] = React.useState(false);
    const [recordsExpanded, setRecordsExpanded] = React.useState(false);
    const [planExpanded, setPlanExpanded] = React.useState(false);
    const [programs, setPrograms] = React.useState(null);  // null = not loaded yet

    const expandPlan = (e) => {
        e.stopPropagation();  // do not close the drawer
        const expanded = !planExpanded;
        setPlanExpanded(expanded);
        if (expanded && bootstrapData.panel) {  // reload on every expand: plan may change without page reload
            api.request('cabinet/programs', { panel_id: bootstrapData.panel.id }, { api_url: 'v2' })
                .then((re) => setPrograms(re));
        }
    }

    const expandLabTests = (e) => {
        e.stopPropagation();  // do not close the drawer
        setLabTestsExpanded(!labTestsExpanded);
    }
    const expandRecords = (e) => {
        e.stopPropagation();  // do not close the drawer
        setRecordsExpanded(!recordsExpanded);
    }

    return (
        <Box
            onClick={onItemClick}
            sx={{
                textAlign: 'center',
                pb: 5,
            }}
        >
            <List>
                <ListItem disablePadding>
                    <InstallComponent showDebug={false} />
                </ListItem>
                <TipOfTheDay />
                <ListItem disablePadding>
                    <ListItemButton
                        component={Link}
                        to={route("/")}
                        selected={location.pathname === route("/")}
                    >
                        <ListItemText primary={t("Главная")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        component={Link}
                        to={route("/labs/")}
                        selected={location.pathname === route("/labs/")}
                    >
                        <ListItemText primary={t("Шаг 1: В лабораторию")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        component={Link}
                        to={route("/questions/" + (
                            userFlowState.retakeQuestionsNotice
                                ? "unanswered/" : ""
                        ))}
                        selected={location.pathname === route("/questions/")}
                    >
                        <ListItemText
                            primary={t("Шаг 2: Вопросы")}
                            secondary={ userFlowState.retakeQuestionsNotice ?  t("Есть неотвеченные вопросы") : null }
                            secondaryTypographyProps={{ sx: { color: "red" }, }}
                        />
                    </ListItemButton>
                </ListItem>

                <Tooltip title={userFlowState.stepLabTestsActive ? "" : t("Сначала ответьте на все вопросы")}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={userFlowState.stepLabTestsActive ? route("/lab-tests/") : ""}
                            selected={location.pathname === route("/lab-tests/")}
                            disabled={!userFlowState.stepLabTestsActive}
                        >
                            <ListItemText primary={t("Шаг 3: Анализы")} />
                        </ListItemButton>

                        {userFlowState.showPanelsAndDynamicsInMenu &&
                            <ListItemButton onClick={expandLabTests} sx={{ flexGrow: 0 }}>
                                {labTestsExpanded ?
                                    <ExpandMoreIcon />
                                    :
                                    <ChevronRightIcon />
                                }
                            </ListItemButton>
                        }
                    </ListItem>
                </Tooltip>
                {labTestsExpanded &&
                    <>
                        {showDynamicsPage &&
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to={route("/dynamics/")}
                                    selected={location.pathname === route("/dynamics/")}
                                    sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                                >
                                    <InsightsIcon sx={{ mr: 1 }} />
                                    <ListItemText primary={t("Динамика по панелям")} />
                                </ListItemButton>
                            </ListItem>
                        }
                        <ListItem disablePadding>
                            <ListItemButton
                                component={Link}
                                to={route("/panels/")}
                                selected={location.pathname === route("/panels/")}
                                sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                            >
                                <AutoAwesomeMotionOutlinedIcon sx={{ mr: 1 }} />
                                <ListItemText primary={t("Мои панели")} />
                            </ListItemButton>
                        </ListItem>
                        {featureEnabled('free_measurements') &&
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to={route("/free/")}
                                    selected={location.pathname.startsWith(route("/free/"))}
                                    sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                                >
                                    <BubbleChartIcon sx={{ mr: 1 }} />
                                    <Box sx={{display: 'flex', flexFlow: 'column nowrap', gap: 0}}>
                                        <ListItemText primary={t("Личные замеры")} />
                                        <Box sx={{ fontSize: '12px', color: '#954', mt: -1 }}>{t("альфа версия")}</Box>
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        }
                    </>
                }
                <Tooltip title={userFlowState.stepRisksActive ? "" : t("Сначала заполните хотя бы некоторые анализы")}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={userFlowState.stepRisksActive ? route("/risks/") : ""}
                            selected={location.pathname === route("/risks/")}
                            disabled={!userFlowState.stepRisksActive}
                        >
                            <ListItemText primary={t("Шаг 4: Риски")} />
                        </ListItemButton>
                    </ListItem>
                </Tooltip>
                <Tooltip title={userFlowState.stepAgesActive ? "" : t("Сначала пройдите предыдущий шаг")}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={userFlowState.stepAgesActive ? route("/age-assessments/") : ""}
                            selected={location.pathname === route("/age-assessments/")}
                            disabled={!userFlowState.stepAgesActive}
                        >
                            <ListItemText primary={t("Шаг 5: Оценка возраста")} />
                        </ListItemButton>
                    </ListItem>
                </Tooltip>
                <Tooltip title={userFlowState.stepPlanActive ? "" : t("Сначала пройдите предыдущий шаг")}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={userFlowState.stepPlanActive ? route("/plan/") : ""}
                            selected={location.pathname === route("/plan/")}
                            disabled={!userFlowState.stepPlanActive}
                        >
                            <ListItemText primary={t("Шаг 6: План")} />
                        </ListItemButton>
                        <ListItemButton onClick={expandPlan} sx={{ flexGrow: 0 }}>
                            {planExpanded ?
                                <ExpandMoreIcon />
                                :
                                <ChevronRightIcon />
                            }
                        </ListItemButton>
                    </ListItem>
                </Tooltip>
                {planExpanded && featureEnabled('experimental') && programs !== null && programs.map((program) => (
                    <ListItem disablePadding key={program.tag}>
                        <ListItemButton
                            component={Link}
                            to={route("/plan/" + program.tag + "/")}
                            selected={location.pathname === route("/plan/" + program.tag + "/")}
                            sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                        >
                            <ListItemText primary={program.name[i18n.language] || program.name.ru || program.tag} />
                        </ListItemButton>
                    </ListItem>
                ))}
                <Tooltip title={userFlowState.stepNextActive ? "" : t("Сначала пройдите предыдущий шаг")}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={userFlowState.stepNextActive ? route("/next/") : ""}
                            selected={location.pathname === route("/next/")}
                            disabled={!userFlowState.stepNextActive}
                        >
                            <ListItemText primary={t("Шаг 7: Что дальше")} />
                        </ListItemButton>
                    </ListItem>
                </Tooltip>
                {(featureEnabled('sponsorship') || featureEnabled('earlyaccess')) &&
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={route("/records/")}
                            selected={location.pathname === route("/records/")}
                        >
                            {featureEnabled('sponsorship') && <WorkspacePremiumIcon sx={{ mr: 1,color:'green' }} titleAccess={t('Доступно только для спонсоров')}/>}
                            <ListItemText primary={t("Данные")} />
                        </ListItemButton>

			{featureEnabled('experimental') &&
                        <ListItemButton onClick={expandRecords} sx={{ flexGrow: 0 }}>
                            {recordsExpanded ?
                                <ExpandMoreIcon />
                                :
                                <ChevronRightIcon />
                            }
                        </ListItemButton>
			}
                    </ListItem>
                }
                {recordsExpanded && <>
                    { featureEnabled('experimental') &&
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={route("/records/input")}
                            selected={location.pathname === route("/records/input")}
                            sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                        >
                            <ListItemText primary={t("Ввод")} />
                        </ListItemButton>
                    </ListItem>
		    }
                    { false && <>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={route("/records/")}
                            selected={location.pathname === route("/records2/")}
                            sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                        >
                            <ListItemText primary="Импорт" />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={route("/records/")}
                            selected={location.pathname === route("/records3/")}
                            sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                        >
                            <ListItemText primary="Экспорт" />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={route("/records/")}
                            selected={location.pathname.startsWith(route("/records4/"))}
                            sx={{ pl: submenuOffset, pt: 0.5, pb: 0.5, color: theme.palette.primary.main }}
                        >
                            <ListItemText primary="Справочники" />
                        </ListItemButton>
                    </ListItem>
                    </>}
                </>}
                <ListItem disablePadding>
                    <ListItemButton
                        component={Link}
                        to={route("/chatbot/")}
                        selected={location.pathname === route("/chatbot/")}
                    >
                        <ListItemText primary={t("Чатбот")} />
                    </ListItemButton>

                </ListItem>
                <ListItem>
                    <UpdateAnnouncements />
                </ListItem>
            </List>

            <SupportLinks />

            <AccountActions />

            <DebugToosLink />

            <Divider sx={{mt: 3, mb: 2}} />
            <MUILink variant="text" component={Link} to={route('/company-details/')}
                     sx={{
                         fontSize: "12px",
                         textDecoration: "none",
                     }}>
                {t('ОсОО "Нестарение Инвестмент"')}
            </MUILink>
        </Box>
    );
});
